import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../../../component/breadcrumb";
import PersonalInfoSection from "../../userManagement/userComponent/personalInformationSection";
import TabSectionView from "../../userManagement/userComponent/tabSectionView";
import { iconMap } from "../../../../constants/userManagement";
import axiosInstance from "../../../../configuration/request";
import { useSnackbar } from "../../../../component/SnackbarContext";
import { FaUser } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { CircularProgress } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

const BCrumb = [
  { to: "/app/customers", title: "Customers" },
  { title: "View Customer" },
];

function formatAddress(address) {
  if (!address) return "";
  return [
    address.house_no,
    address.street_address,
    address.landmark,
    address.city,
    address.state,
    address.zipcode,
  ]
    .filter(Boolean)
    .join(", ");
}

export default function CustomerView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [personalInformation, setPersonalInformation] = useState({});
  const [accountDetails, setAccountDetails] = useState({});
  const [addressRows, setAddressRows] = useState([]);
  const [bankData, setBankData] = useState({});
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axiosInstance
      .get(`/customer/users?id=${id}`)
      .then((res) => {
        const data = res?.data?.data?.[0];
        if (!data) {
          showSnackbar("Customer not found", "error");
          navigate("/app/customers", { replace: true });
          return;
        }
        setCustomer(data);

        const primaryAddress = data?.addresses?.[0];
        setPersonalInformation({
          "Full Name": data?.name || "-",
          "Birth Date": data?.dob || "-",
          Email: data?.email || "-",
          Gender: data?.gender || "-",
          "Mobile Number": data?.phone_number || "-",
          Address: formatAddress(primaryAddress) || "-",
        });

        setAccountDetails({
          "User ID": data?.id ?? "-",
          Role: data?.userType || "normal user",
          Status: data?.user_status ? "Active" : "Inactive",
          "Joined On": data?.created_at
            ? new Date(data.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-",
          "Approval Status": data?.isApproved || "-",
        });

        const addresses = (data?.addresses || []).map((addr, index) => ({
          [`Address ${index + 1}`]: `${formatAddress(addr)}${
            addr?.is_primary ? " (Primary)" : ""
          }${addr?.address_type ? ` · ${addr.address_type}` : ""}`,
        }));
        const mergedAddresses = addresses.reduce(
          (acc, item) => ({ ...acc, ...item }),
          {}
        );
        setAddressRows(
          Object.keys(mergedAddresses).length
            ? mergedAddresses
            : { Addresses: "No saved addresses" }
        );

        const bank = data?.bankDetails?.[0];
        setBankData({
          "Account Number": bank?.accountNumber || "-",
          "Account Name": bank?.accountName || "-",
          "Bank Name": bank?.bankName || "-",
          "Routing Number": bank?.routingNumber || "-",
        });
      })
      .catch(() => {
        showSnackbar("Failed to load customer details", "error");
        navigate("/app/customers", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, navigate, showSnackbar]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <CircularProgress size={48} sx={{ color: "var(--primary-color)" }} />
        <p className="mt-3 text-sm text-[var(--light-text-color)]">
          Loading customer…
        </p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-95px)] overflow-y-auto hide-scrollbar">
      <div className="flex justify-between items-center mb-3 ml-4 mr-4 sm:ml-6">
        <Breadcrumb title="" items={BCrumb} />
      </div>

      <div className="flex flex-col xl:flex-row mx-4 gap-4">
        <div className="w-full xl:w-[40%] bg-white rounded-[16px] shadow-md overflow-hidden border border-[#F1F1EF] h-auto xl:h-[calc(100vh-150px)]">
          <div
            className="h-[150px] w-full"
            style={{
              background:
                "linear-gradient(to right, #B2D8F7, #9CC8E2, #3383B2)",
            }}
          />
          <div className="-mt-[75px] mb-1 pl-6">
            <div className="relative w-[120px] h-[120px] border-[4px] border-white rounded-full overflow-hidden shadow-md flex items-center justify-center bg-gray-100">
              {customer?.profile_img_url ? (
                <>
                  {!isImageLoaded && (
                    <ImSpinner2 className="absolute animate-spin text-gray-400 text-3xl" />
                  )}
                  <img
                    src={customer.profile_img_url}
                    alt="Profile"
                    className={`h-full w-full object-cover transition-opacity duration-300 ${
                      isImageLoaded ? "opacity-100" : "opacity-0"
                    }`}
                    onLoad={() => setIsImageLoaded(true)}
                  />
                </>
              ) : (
                <FaUser className="text-gray-400 text-5xl" />
              )}
            </div>
          </div>
          <PersonalInfoSection
            iconMap={iconMap}
            personalInfo={personalInformation}
          />
        </div>

        <div className="w-full xl:w-[60%] flex flex-col gap-3 pb-6">
          <TabSectionView
            title="Account Details"
            icon={HomeIcon}
            data={accountDetails}
          />
          <TabSectionView
            title="Saved Addresses"
            icon={HomeIcon}
            data={addressRows}
          />
          <TabSectionView
            title="Bank Details"
            icon={AccountBalanceIcon}
            data={bankData}
          />
        </div>
      </div>
    </div>
  );
}
